import {GRANITE_2} from '@gaia/colors';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {
    coordinatesToUsersFormat,
    formatElevation,
    formatRawElevation,
} from '@gaia/utils/formatters';
import fetchElevation, {NO_DATA} from '@gaia/utils/gaia/fetchElevation';
import CopyCoordinates from '@map/sidebars/CopyCoordinates';
import {ListItem} from '@mui/material';
import useSWRImmutable from 'swr/immutable';

import Typography from './SidebarTypography';

interface WaypointStatsProps {
    waypoint: Waypoint | PartialWaypoint;
    isDragging?: boolean;
    compact?: boolean;
    title?: string;
    action?: React.ReactNode;
}

export default function WaypointStats({
    waypoint,
    isDragging,
    compact,
    title,
    action,
}: WaypointStatsProps) {
    const {coordinateFormat, unitSystem} = useUser();

    // POIs may include elevation in their properties; we skip our elevation lookup in this case
    const waypointElevation =
        unitSystem === 'metric' ? waypoint?.properties.ele_meters : waypoint?.properties.ele_feet;

    const {data: elevation} = useSWRImmutable<GeoJSON.Point>(
        waypoint && !waypointElevation ? waypoint.geometry : null,
        fetchElevation
    );

    const coordinates = coordinatesToUsersFormat(waypoint?.geometry.coordinates, coordinateFormat);

    const hasLinebreak = coordinateFormat !== 'dd';

    const displayCoordinates = hasLinebreak ? coordinates.split(', ') : [coordinates];

    const getElevation = () => {
        if (waypointElevation) return formatRawElevation(waypointElevation, unitSystem);
        if (!elevation || !elevation.coordinates || isDragging) return '...';
        if (elevation.coordinates[2] === NO_DATA) return 'Unavailable';
        return formatElevation(elevation.coordinates[2], unitSystem);
    };

    if (compact) {
        return (
            <div style={{display: 'flex', flexDirection: 'row'}}>
                <div style={{flex: 1}}>
                    <Typography.Body2 bold>{title}</Typography.Body2>
                    <Typography.Caption>
                        {coordinates}
                        {hasLinebreak ? <br /> : ' • '}
                        {`Elevation: ${getElevation()}`}
                    </Typography.Caption>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: theme.spacing(-1),
                    }}
                >
                    {action !== undefined ? action : <CopyCoordinates coordinates={coordinates} />}
                </div>
            </div>
        );
    }

    return (
        <>
            <ListItem
                component='div'
                divider
                disableGutters
                style={{
                    paddingTop: theme.spacing(1.25),
                    paddingBottom: theme.spacing(1.25),
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: GRANITE_2,
                    }}
                >
                    <Typography.Body2>Elevation</Typography.Body2>
                    <Typography.Body1 bold>{getElevation()}</Typography.Body1>
                </div>
            </ListItem>

            <ListItem
                component='div'
                divider
                disableGutters
                style={{
                    paddingTop: theme.spacing(1.25),
                    paddingBottom: theme.spacing(1.25),
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: GRANITE_2,
                    }}
                >
                    <Typography.Body2>Coordinates</Typography.Body2>

                    <div style={{display: 'flex'}}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                textAlign: 'right',
                                height: hasLinebreak ? 48 : 'auto',
                            }}
                        >
                            {isDragging && <Typography.Body1 bold>...</Typography.Body1>}

                            {!isDragging &&
                                displayCoordinates.map((part) => (
                                    <Typography.Body1 bold key={part}>
                                        {part}
                                    </Typography.Body1>
                                ))}
                        </div>

                        <CopyCoordinates
                            coordinates={coordinates}
                            style={{
                                marginLeft: theme.spacing(1),
                                alignSelf: 'center',
                            }}
                        />
                    </div>
                </div>
            </ListItem>
        </>
    );
}
